/**
 * AI Workflow Node Edit Modal
 *
 * Allows users to edit workflow nodes before execution:
 * - Edit prompts and system prompts
 * - Modify display name and description
 * - Adjust model selection and complexity
 * - View node metadata
 *
 * Only editable for nodes in pending/ready status.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  X,
  Save,
  AlertCircle,
  Sparkles,
  FileText,
  Settings,
  Zap,
  Info,
  Database,
  GitMerge,
  User,
  ListTree,
  RefreshCw
} from 'lucide-react';

/**
 * Get node type icon
 */
const getNodeTypeIcon = (nodeType) => {
  const icons = {
    data_retrieval: Database,
    llm_analysis: Sparkles,
    reconciliation: GitMerge,
    human_approval: User,
    decomposition: ListTree
  };
  return icons[nodeType] || Sparkles;
};

/**
 * Get node type color
 */
const getNodeTypeColor = (nodeType) => {
  const colors = {
    data_retrieval: '#3B82F6',    // blue
    llm_analysis: '#8B5CF6',      // purple
    reconciliation: '#F97316',    // orange
    human_approval: '#EAB308',    // yellow
    decomposition: '#14B8A6'      // teal
  };
  return colors[nodeType] || '#8B5CF6';
};

/**
 * Build a concise description string from model metadata.
 */
const buildModelDescription = (model) => {
  const ctx = model.context_length
    ? `${Math.round(model.context_length / 1000)}k ctx`
    : '';
  return ctx || '';
};

/**
 * Modal component for editing workflow nodes
 */
export default function AIWorkflowNodeEditModal({ node, isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    displayName: '',
    description: '',
    promptTemplate: '',
    systemPrompt: '',
    selectedModel: '',
    complexityScore: 5,
    estimatedTokens: 0,
    requiresApproval: false,
    approvalPrompt: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('prompts'); // prompts, metadata, advanced

  // Dynamic model list from aiservice
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  const fetchModels = useCallback(async () => {
    setModelsLoading(true);
    try {
      const response = await axios.get('/api/v1/models');
      const models = response.data?.models || [];
      // Filter to text-output models only (skip image-gen, etc.)
      const textModels = models.filter((m) => {
        const modality = m.architecture?.modality || 'text->text';
        return modality.includes('text') && !modality.endsWith('->image');
      });
      // Sort by provider then name
      textModels.sort((a, b) => (a.id || '').localeCompare(b.id || ''));
      setAvailableModels(textModels);
    } catch (err) {
      console.error('Failed to fetch models:', err);
      // Keep whatever we had before so the dropdown isn't empty
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // Fetch models when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchModels();
    }
  }, [isOpen, fetchModels]);

  // Initialize form when node changes
  useEffect(() => {
    if (node && isOpen) {
      setFormData({
        displayName: node.data?.displayName || '',
        description: node.data?.description || '',
        promptTemplate: node.data?.prompt_template || '',
        systemPrompt: node.data?.system_prompt || '',
        selectedModel: node.data?.selected_model || '',
        complexityScore: node.data?.complexity || 5,
        estimatedTokens: node.data?.estimatedTokens || 0,
        requiresApproval: node.data?.requiresApproval || false,
        approvalPrompt: node.data?.approvalPrompt || ''
      });
      setErrors({});
      setActiveTab('prompts');
    }
  }, [node, isOpen]);

  if (!isOpen || !node) return null;

  const nodeData = node.data || {};
  const nodeType = nodeData.nodeType || 'llm_analysis';
  const status = nodeData.status || 'pending';
  const TypeIcon = getNodeTypeIcon(nodeType);
  const typeColor = getNodeTypeColor(nodeType);

  // Check if node is editable (only pending or ready nodes)
  const isEditable = status === 'pending' || status === 'ready';

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }

    if (nodeType === 'llm_analysis' && !formData.promptTemplate.trim()) {
      newErrors.promptTemplate = 'Prompt template is required for LLM nodes';
    }

    if (formData.requiresApproval && !formData.approvalPrompt.trim()) {
      newErrors.approvalPrompt = 'Approval prompt is required when approval is enabled';
    }

    if (formData.complexityScore < 0 || formData.complexityScore > 10) {
      newErrors.complexityScore = 'Complexity must be between 0 and 10';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await onSave(node.id, {
        display_name: formData.displayName,
        description: formData.description,
        prompt_template: formData.promptTemplate,
        system_prompt: formData.systemPrompt,
        selected_model: formData.selectedModel,
        complexity_score: formData.complexityScore,
        estimated_tokens: formData.estimatedTokens,
        requires_approval: formData.requiresApproval,
        approval_prompt: formData.approvalPrompt
      });
      onClose();
    } catch (error) {
      console.error('Error saving node:', error);
      setErrors({ general: error.message || 'Failed to save node changes' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        // Close modal only if clicking backdrop (not content)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b border-gray-200 flex items-center justify-between"
          style={{ borderTopColor: typeColor, borderTopWidth: '4px' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${typeColor}20` }}
            >
              <TypeIcon size={24} style={{ color: typeColor }} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditable ? 'Edit Node' : 'View Node'}
              </h2>
              <p className="text-sm text-gray-600">
                {nodeData.nodeKey} • {status}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Non-editable warning */}
        {!isEditable && (
          <div className="mx-6 mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium text-yellow-900">Read-only mode</div>
              <div className="text-yellow-700">
                This node cannot be edited because it has already been executed or is currently running.
                Only pending or ready nodes can be modified.
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('prompts')}
              className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'prompts'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <Sparkles size={16} />
                <span>Prompts</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('metadata')}
              className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'metadata'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText size={16} />
                <span>Metadata</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('advanced')}
              className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'advanced'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <Settings size={16} />
                <span>Advanced</span>
              </div>
            </button>
          </div>
        </div>

        {/* Content - scrollable area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* General error */}
          {errors.general && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-700">{errors.general}</div>
            </div>
          )}

          {/* Prompts Tab */}
          {activeTab === 'prompts' && (
            <div className="space-y-6">
              {/* Prompt Template */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Sparkles size={16} />
                  Prompt Template
                  {nodeType === 'llm_analysis' && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <textarea
                  value={formData.promptTemplate}
                  onChange={(e) => handleChange('promptTemplate', e.target.value)}
                  disabled={!isEditable}
                  placeholder="Enter the prompt that will be sent to the AI model..."
                  className={`w-full h-40 px-4 py-3 border rounded-lg resize-none font-mono text-sm ${
                    errors.promptTemplate
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                  } ${!isEditable ? 'bg-gray-50 text-gray-600' : ''}`}
                />
                {errors.promptTemplate && (
                  <p className="mt-1 text-xs text-red-600">{errors.promptTemplate}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  The main instruction sent to the AI model. Use variables like {'{input}'}, {'{context}'} if needed.
                </p>
              </div>

              {/* System Prompt */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <FileText size={16} />
                  System Prompt
                </label>
                <textarea
                  value={formData.systemPrompt}
                  onChange={(e) => handleChange('systemPrompt', e.target.value)}
                  disabled={!isEditable}
                  placeholder="Optional system-level instructions (role, constraints, format)..."
                  className={`w-full h-32 px-4 py-3 border border-gray-300 rounded-lg resize-none font-mono text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                    !isEditable ? 'bg-gray-50 text-gray-600' : ''
                  }`}
                />
                <p className="mt-1 text-xs text-gray-500">
                  System-level instructions that set the context for the AI (e.g., "You are an expert data analyst").
                </p>
              </div>
            </div>
          )}

          {/* Metadata Tab */}
          {activeTab === 'metadata' && (
            <div className="space-y-6">
              {/* Display Name */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  disabled={!isEditable}
                  placeholder="Node display name"
                  className={`w-full px-4 py-2 border rounded-lg ${
                    errors.displayName
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                  } ${!isEditable ? 'bg-gray-50 text-gray-600' : ''}`}
                />
                {errors.displayName && (
                  <p className="mt-1 text-xs text-red-600">{errors.displayName}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  disabled={!isEditable}
                  placeholder="Brief description of what this node does"
                  className={`w-full h-24 px-4 py-3 border border-gray-300 rounded-lg resize-none text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                    !isEditable ? 'bg-gray-50 text-gray-600' : ''
                  }`}
                />
              </div>

              {/* Node Type (read-only) */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Info size={16} />
                  Node Type
                </label>
                <div className="flex items-center gap-2">
                  <div
                    className="px-3 py-2 rounded-lg text-sm font-medium text-white"
                    style={{ backgroundColor: typeColor }}
                  >
                    {nodeType}
                  </div>
                  <span className="text-xs text-gray-500">(Cannot be changed)</span>
                </div>
              </div>

              {/* Depth Level & Order (read-only) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Depth Level
                  </label>
                  <div className="px-4 py-2 bg-gray-100 rounded-lg text-sm text-gray-700">
                    {nodeData.depthLevel || 0}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Order Index
                  </label>
                  <div className="px-4 py-2 bg-gray-100 rounded-lg text-sm text-gray-700">
                    {nodeData.orderIndex || 0}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div className="space-y-6">
              {/* Model Selection */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Zap size={16} />
                  AI Model
                  {modelsLoading && (
                    <RefreshCw size={14} className="animate-spin text-gray-400" />
                  )}
                  {!modelsLoading && (
                    <button
                      type="button"
                      onClick={fetchModels}
                      className="text-gray-400 hover:text-blue-500 transition-colors"
                      title="Refresh model list"
                    >
                      <RefreshCw size={14} />
                    </button>
                  )}
                </label>
                <select
                  value={formData.selectedModel}
                  onChange={(e) => handleChange('selectedModel', e.target.value)}
                  disabled={!isEditable || modelsLoading}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                    !isEditable ? 'bg-gray-50 text-gray-600' : ''
                  }`}
                >
                  {availableModels.length === 0 && (
                    <option value="">
                      {modelsLoading ? 'Loading models...' : 'No models available'}
                    </option>
                  )}
                  {availableModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name || model.id}{buildModelDescription(model) ? ` — ${buildModelDescription(model)}` : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {availableModels.length > 0
                    ? `${availableModels.length} models available from OpenRouter cache.`
                    : 'Choose the AI model based on complexity and cost requirements.'}
                </p>
              </div>

              {/* Complexity Score */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  Complexity Score (0-10)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={formData.complexityScore}
                    onChange={(e) => handleChange('complexityScore', parseInt(e.target.value))}
                    disabled={!isEditable}
                    className="flex-1"
                  />
                  <div className="w-12 text-center font-semibold text-lg text-gray-900">
                    {formData.complexityScore}
                  </div>
                </div>
                {errors.complexityScore && (
                  <p className="mt-1 text-xs text-red-600">{errors.complexityScore}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Higher complexity may warrant more capable (and expensive) models.
                </p>
              </div>

              {/* Estimated Tokens */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  Estimated Tokens
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.estimatedTokens}
                  onChange={(e) => handleChange('estimatedTokens', parseInt(e.target.value) || 0)}
                  disabled={!isEditable}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 ${
                    !isEditable ? 'bg-gray-50 text-gray-600' : ''
                  }`}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Estimated token count for this operation (for cost tracking).
                </p>
              </div>

              {/* Requires Approval */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="requiresApproval"
                    checked={formData.requiresApproval}
                    onChange={(e) => handleChange('requiresApproval', e.target.checked)}
                    disabled={!isEditable}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor="requiresApproval"
                      className="text-sm font-medium text-gray-700 cursor-pointer"
                    >
                      Requires Human Approval
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Workflow will pause before executing this node and wait for user approval.
                    </p>
                  </div>
                </div>

                {/* Approval Prompt (conditional) */}
                {formData.requiresApproval && (
                  <div className="mt-4">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      Approval Prompt <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.approvalPrompt}
                      onChange={(e) => handleChange('approvalPrompt', e.target.value)}
                      disabled={!isEditable}
                      placeholder="What should the user review before approving this step?"
                      className={`w-full h-24 px-4 py-3 border rounded-lg resize-none text-sm ${
                        errors.approvalPrompt
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                      } ${!isEditable ? 'bg-gray-50 text-gray-600' : ''}`}
                    />
                    {errors.approvalPrompt && (
                      <p className="mt-1 text-xs text-red-600">{errors.approvalPrompt}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-xs text-gray-500">
            Node ID: {node.id}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {isEditable ? 'Cancel' : 'Close'}
            </button>
            {isEditable && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save size={16} />
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
